import React, { useState } from 'react';
import type { DescriptionEntry, DescriptionType, LanguageIso } from 'simple-creator/types';
import { RichTextEditor } from './RichTextEditor';

interface TextContentSectionProps {
  subtitle: string;
  onSubtitleChange: (value: string) => void;
  descriptions: DescriptionEntry[];
  onDescriptionsChange: (descriptions: DescriptionEntry[]) => void;
  languageIso?: LanguageIso;
  className?: string;
}

const EXPANDABLE_DESCRIPTION_TYPES: { type: DescriptionType; label: string }[] = [
  { type: 'print-text', label: 'Print Text' },
  { type: 'teaser', label: 'Teaser' },
  { type: 'venue-textual-info', label: 'Venue Info' },
  { type: 'zusatzinfo', label: 'Additional Info' },
];

export const TextContentSection: React.FC<TextContentSectionProps> = ({
  subtitle,
  onSubtitleChange,
  descriptions,
  onDescriptionsChange,
  languageIso = 'de',
  className = '',
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  // Filter out main/subtitle types that should not be displayed here
  const EXCLUDED_TYPES = ['main', 'subtitle', 'collective-description-body'];
  const filteredDescriptions = descriptions.filter(
    (d) => !EXCLUDED_TYPES.includes(d.type)
  );
  
  const addedTypes = filteredDescriptions.map((d) => d.type);
  const availableTypes = EXPANDABLE_DESCRIPTION_TYPES.filter(
    (dt) => !addedTypes.includes(dt.type)
  );

  const handleAddDescription = (type: DescriptionType) => {
    const newDescription: DescriptionEntry = {
      languageIso,
      type,
      contentType: 'html',
      content: '',
    };
    onDescriptionsChange([...descriptions, newDescription]);
    setShowDropdown(false);
  };

  const handleUpdateDescription = (type: DescriptionType, content: string) => {
    const updated = descriptions.map(d => 
      d.type === type ? { ...d, content } : d
    );
    onDescriptionsChange(updated);
  };

  const handleRemoveDescription = (type: DescriptionType) => {
    onDescriptionsChange(descriptions.filter((d) => d.type !== type));
  };

  const getLabelForType = (type: DescriptionType): string => {
    return EXPANDABLE_DESCRIPTION_TYPES.find((dt) => dt.type === type)?.label || type;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Subtitle - always visible */}
      <div>
        <label className="label block mb-2">
          Subtitle <span className="text-gray-400">(optional)</span>
        </label>
        <input
          type="text"
          value={subtitle}
          onChange={(e) => onSubtitleChange(e.target.value)}
          placeholder="Add a catchy subtitle"
          className="input"
          maxLength={150}
        />
      </div>

      {/* Added description fields */}
      {filteredDescriptions.map((desc) => (
        <div key={desc.type} className="relative">
          <label className="label block mb-2 flex items-center justify-between">
            <span>{getLabelForType(desc.type)}</span>
            <button
              type="button"
              onClick={() => handleRemoveDescription(desc.type)}
              className="text-gray-400 hover:text-red-500 text-sm"
              aria-label={`Remove ${getLabelForType(desc.type)}`}
            >
              ✕ Remove
            </button>
          </label>
          <RichTextEditor
            value={desc.content}
            onChange={(html) => handleUpdateDescription(desc.type, html)}
            placeholder={`Enter ${getLabelForType(desc.type).toLowerCase()}...`}
            minHeight="100px"
          />
        </div>
      ))}

      {/* Add more dropdown */}
      {availableTypes.length > 0 && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full py-3 text-balloon border-2 border-dashed border-balloon/50 hover:border-balloon hover:bg-balloon/5 rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
          >
            <span className="text-xl">+</span>
            <span>Add more text content...</span>
          </button>

          {showDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              {availableTypes.map((dt) => (
                <button
                  key={dt.type}
                  type="button"
                  onClick={() => handleAddDescription(dt.type)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg transition-colors"
                >
                  {dt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
